<template>
  <div class="opc-cart" :class="{
    'opc-cart--no-title': !showTitle,
    'opc-modal': $context.is.modal
  }">
    <singleton>
      <prorated-main />
    </singleton>
    <div class="opc-cart__upper">
      <support-coupon-banner />
      <div v-if="showTitle" class="opc-cart__title text-capitalize">
        <h2>{{ title }}</h2>
        <template v-if="!onlyShowTitle">
          <ShareLink v-if="showShareLinkAfterTitle" />
          <currency v-if="!$config('hideCurrency', false)" />
        </template>
      </div>
      <div class="opc-cart__main">
        <plan-count v-if="$context.is.desktop" />
        <product-details class='opc-cart__main__cartinfo __PLACEHOLDER_cart__' render-price :filter-keys="filterKeys" @edit="onEdit" @remove="onRemove" />
        <partner-plan-cart />
        <open-invoice-cart
          :open-invoice-list="openInvoiceList"
          :open-invoice-total-amount="openInvoiceTotalAmount"
          :open-invoice-original-total-amount="openInvoiceOriginalTotalAmount"
          :current-credit-balance="getterCurrentCreditBalance"
        />
        <div v-if="isCurrentMode">
          <alt-prorated-current-summary>
            <div class="opc-cart__main__divider"></div>
            <current-charge />
          </alt-prorated-current-summary>
        </div>
        <div class='__PLACEHOLDER_total__' v-else>
          <product-details render-agreement :filter-keys="filterKeys" />
          <product-details render-tip :filter-keys="filterKeys" />
          <prorated-charge v-if="showProratedCharge" />
          <div class="opc-cart__main__divider"></div>
          <coupon v-if="hasSupportCoupon" />
          <alt-prorated-total>
            <total />
          </alt-prorated-total>
          <div class="opc-cart__main__divider" v-if="!showProratedCharge"></div>
          <next-charge v-if="$context.is.buyNow || $context.is.basic2pro || $context.is.add2trial || $context.is.buyTrial" />
          <!-- placeholder for new confirmation page -->
          <div v-show='false' class='__PLACEHOLDER_0__'></div>
          <alt-prorated-description>
            <description />
          </alt-prorated-description>
          <alt-prorated-updated-summary />
        </div>
      </div>
    </div>
    <div class="opc-cart__footer" v-if="$context.is.desktop">
      <div class="opc-cart__submit __PLACEHOLDER_submit__">
        <alt-prorated-today>
          <todays-total v-if="!isCurrentMode">
            <h3 class="opc-cart__submit__label--left">{{ submitLabel }}</h3>
            <h3 class="opc-cart__submit__label--right">
              <!-- // FREE TRIAL  -->
              <span v-if="isFreeTrialCharge">
              {{ this.$money(0) }}
            </span>
              <i v-else-if="isLoadingTax || isLoadingPreview" class="zm-icon-loading" />
              <!-- // FREE TRIAL - showProratedCharge won't be shown at the same time with Free Trial price -->
              <span v-else-if='showProratedCharge'>
              {{ this.$money(this.getProratedChargeTotalAmount) }}
            </span>
              <span v-else>
              {{ this.$money(includeTaxTotalAmount) }}
              <p class="opc-cart__total__text__sub text-no-capitalize" v-if="showPlusTaxTxt">
                <priceVATTip />
                <template v-if="!showVATTip">{{ $t('billing.order_summary.plus_applicable_taxes') }}</template>
              </p>
            </span>
            </h3>
          </todays-total>
        </alt-prorated-today>
        <slot name="action" />
      </div>
    </div>
  </div>
</template>

<script>
import ProductDetails from './details.vue';
import ProratedCharge from './prorated-charge.vue';
import Total from './total.vue';
import Coupon from '@/views/config/coupon/index.vue';
import Description from './description.vue';
import { mapGetters } from 'vuex';
import Currency from './currency.vue';
import NextCharge from './next.vue';
import nextMixin from './nextMixin';
import PlanCount from '@/views/config/shopping-cart/plan-count';
import CurrentCharge from './current.vue';
import ShareLink from './share-link.vue';
import ProratedMain from '@/views/config/shopping-cart/prorated/index.vue';
import AltProratedDescription from '@/views/config/shopping-cart/prorated/description.vue';
import AltProratedCurrentSummary from '@/views/config/shopping-cart/prorated/summary-current.vue';
import AltProratedUpdatedSummary from '@/views/config/shopping-cart/prorated/summary-updated.vue';
import AltProratedTotal from '@/views/config/shopping-cart/prorated/total.vue';
import AltProratedToday from '@/views/config/shopping-cart/prorated/today.vue';
import { VIEWS } from '@/common/constants.js';
import flow from '@/flows';
import PartnerPlanCart from '@/views/config/partner-refer/plans/cart.vue';
import { isIncreasePriceForPH } from '@/common/hardcode';
import Singleton from '@/components/singleton.vue';
import OpenInvoiceCart from '@/views/config/shopping-cart/open-invoice/open-invoice-cart.vue';
import SupportCouponBanner from '@/views/config/coupon/component/support-coupon/redeem-baner.vue';
import TodaysTotal from '@/views/config/shopping-cart/todays-total.vue';

export default {
  mixins: [nextMixin],
  components: {
    TodaysTotal,
    SupportCouponBanner,
    OpenInvoiceCart,
    PartnerPlanCart,
    PlanCount,
    Currency,
    ProductDetails,
    ProratedCharge,
    Total,
    CurrentCharge,
    Description,
    Coupon,
    NextCharge,
    ShareLink,
    ProratedMain,
    AltProratedDescription,
    AltProratedUpdatedSummary,
    AltProratedCurrentSummary,
    AltProratedTotal,
    AltProratedToday,
    Singleton,
  },
  props: {
    filterKeys: {
      type: Array,
      default: () => [],
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    onlyShowTitle: {
      type: Boolean,
      default: false,
    },
    showShareLinkAfterTitle: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters([
      'getTotalCharge',
      'getTaxTotalAmount',
      'getSelectedPlans',
      'isLoadingTax',
      'isSelectedSameAsEditablePlansInBought',
      'fromCampaignLink',
      'mixBuyFlowContainsEditCase',
      'migratePlanState',
      'getProratedChargeTotalAmount',
      'isLoadingPreview',
      'isInrCitEligible',
      'isProratedChargeAmountCalculated',
      'isTaxEstimatedBefore',
      'addressInfo',
      'openInvoiceList',
      'openInvoiceTotalAmount',
      'openInvoiceOriginalTotalAmount',
      'getterCurrentCreditBalance',
      'includeTaxTotalAmount',
      'haveVerifiedSupportCoupon',
      'accountWithCouponDiscount',
      'couponState'
    ]),
    isFreeTrialFlow() {
      return this.$context.is.add2trial || this.$context.is.buyTrial;
    },
    isShowTax() {
      // FREE TRIAL - won't show tax fee for free trial, except it's under FCCA compliance
      if (this.isFreeTrialFlow && this.addressInfo.country !== 'DE') {
        return false;
      }
      // isTaxExempt in zuora account is only part of the whole tax amount
      if (flow.view() === VIEWS.COMPOUND) {
        return true;
      }
      if (this.$context.is.configPage) {
        if (this.isTaxEstimatedBefore) return true;
        return false;
      }
      return true;
    },
    isCurrentMode() {
      // display as current plans mode or updated mode
      return this.isSelectedSameAsEditablePlansInBought && !this.fromCampaignLink && !this.haveVerifiedSupportCoupon;
    },
    plansCount() {
      return this.getSelectedPlans.length;
    },
    title() {
      if (this.$context.is.delta) return this.$t("billing.order_summary");
      if (this.migratePlanState.is.upgrade) {
        return this.$t('billing.order_summary_updated');
      }
      if (this.$context.is.edit) {
        if (this.isCurrentMode) {
          return this.$t('billing.order_summary_current');
        } else {
          return this.$t('billing.order_summary_updated');
        }
      }
      if(this.$context.is.downgrade || this.$context.is.upgrade) {
        return this.$t('billing.order_summary_updated');
      }

      if (this.$context.is.mixBuy && this.mixBuyFlowContainsEditCase) {
        return this.$t('billing.order_summary_updated');
      }

      return this.$t('billing.order_summary');
    },
    submitLabel(){
      if (this.showProratedCharge) {
        return this.$t('billing.order_summary.todays_total');
      }
      if(this.$context.is.pro2biz || this.$context.is.edit || this.mixBuyFlowContainsEditCase || this.$context.is.add2cart || this.migratePlanState.is.upgrade || this.migratePlanState.is.downgrade || this.$context.is.zs2ze) return this.mx_upgrade_nextChargeLabel;
      return this.$t('billing.order_summary.todays_total');
    },
    hasSupportCoupon() {
      return !isIncreasePriceForPH() && this.getSelectedPlans.find(item => item.supportCoupon);
    },
    isFreeTrialCharge() {
      return this.getSelectedPlans.every(plan => !!plan.isOnlineFreeTrial); // ? some or every or is flow
    },
    showProratedCharge() {
      if (this.$context.is.delta) return true;
      return this.isInrCitEligible && this.isProratedChargeAmountCalculated;
    },
    showPlusTaxTxt(){
      const is0total = ( this.getTotalCharge + this.getTaxTotalAmount === 0 )
      return !(this.isShowTax && this.isTaxEstimatedBefore) && !is0total
    }
  },
  methods: {
    onEdit(...args) {
      this.$emit('edit', ...args);
    },
    onRemove(...args) {
      this.$emit('remove', ...args);
    },
  },
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
